import { Injectable } from '@angular/core';

import { PrettyUrlsConfig, Sitecore, TimeFilterConfig } from '@frontend/sports/common/client-config-data-access';
import { SportConstant } from '@frontend/sports/common/core/data-access/constants';
import { CountItem, ItemType, count, isItemType, takeTop } from '@frontend/sports/common/core/data-access/sport-model';
import { CalendarUrlParam, SportUrlParam, UrlHelperService } from '@frontend/sports/navigation/core/feature/url-helper';
import { RacingUrlParam } from '@frontend/sports/racing/feature/models';

import { CalendarService } from '../calendar/calendar.service';
import { LazyTreeItem, SportTreeItem, TreeOptions } from './competition-list.models';
import { CompetitionRouteService } from './competition-route.service';

@Injectable({ providedIn: 'root' })
export class SportTreeService {
    constructor(
        private competitionRoute: CompetitionRouteService,
        private urlHelper: UrlHelperService,
        private urlConfig: PrettyUrlsConfig,
        private sitecore: Sitecore,
        private calendar: CalendarService,
        private timeFilterConfig: TimeFilterConfig,
    ) {}

    get(sports: CountItem[], excludeContext?: boolean): SportTreeItem[] {
        if (!sports) {
            return [];
        }

        const options = this.getOptions(excludeContext);

        return sports.map((sport) => new SportTreeItem(sport, options));
    }

    getTop(sports: CountItem[]): SportTreeItem {
        const options = this.getOptions();
        const output: SportTreeItem = new LazyTreeItem([], { urlMapper: () => '', countMapper: () => 0 });

        output.title = this.sitecore.lobby.TopSports;
        output.subTitle = this.sitecore.lobby.AzSports;
        output.children = takeTop(sports).map((sport) => new SportTreeItem(sport, options));

        output.children.forEach((child) => {
            child.count = undefined;
            child.tracking = 'TopSportsLinks';
        });

        return output;
    }

    private getContext(): string | undefined {
        return this.competitionRoute.params().context;
    }

    private getOptions(excludeContext?: boolean): TreeOptions {
        const interval = this.calendar.isInterval(this.getContext());

        const options: TreeOptions = {
            urlMapper: this.getUrl(),
            countMapper: this.getCount(interval),
        };

        if (!excludeContext) {
            const context = this.getContext();
            const subContext = interval && !this.timeFilterConfig.isTimeFilterEnabled ? SportUrlParam.Competitions : undefined;

            options.urlMapper = this.getUrl(context, subContext);
        }

        return options;
    }

    private getUrl(context?: string, subContext?: SportUrlParam.Competitions): (sport?: CountItem) => string {
        return (sport) => {
            if (isItemType(sport, ItemType.Sport)) {
                if (sport.isVirtual) {
                    return this.urlHelper.getVirtualSportUrl(sport);
                }

                if (sport.id === SportConstant.EsportsGroup) {
                    return this.urlHelper.getESportsUrl();
                }

                if (sport.id === SportConstant.Horses || sport.id === SportConstant.Greyhounds) {
                    const tomorrow = [CalendarUrlParam.Tomorrow, this.urlConfig.translations.tomorrow];
                    const future = [
                        CalendarUrlParam.After2days,
                        CalendarUrlParam.After3days,
                        this.urlConfig.translations.after2days,
                        this.urlConfig.translations.after3days,
                    ];

                    if (context) {
                        if (tomorrow.includes(context)) {
                            return this.urlHelper.getRaceUrl(sport.id, RacingUrlParam.Tomorrow);
                        }
                        if (future.includes(context)) {
                            return this.urlHelper.getRaceUrl(sport.id, RacingUrlParam.Future);
                        }
                    }

                    return this.urlHelper.getSportUrl(sport);
                }
            }

            return this.urlHelper.getSportUrl(sport, context, subContext);
        };
    }

    private getCount(interval: boolean): (item?: CountItem) => number {
        return (item) => count(item, interval);
    }
}
